import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Search,
  Layers,
  ShieldCheck,
  FileText,
  Compass,
  Mail,
  Plus,
  Minus,
  Check,
} from "lucide-react";
import Seo from "@/components/Seo";
import { useI18n } from "@/lib/i18n";
import { langPath, THANK_YOU_PATH } from "@/lib/routes";

const ORIGIN = "https://databridgar.com";

const IMG = {
  hero: "https://images.pexels.com/photos/8428063/pexels-photo-8428063.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  about:
    "https://images.pexels.com/photos/12899096/pexels-photo-12899096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  workspace:
    "https://images.unsplash.com/photo-1561172472-4f2d94f35e87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHx3b3Jrc3BhY2UlMjBsYXB0b3AlMjBub3RlYm9va3xlbnwwfHx8d2hpdGV8MTc4ODMyOTA2MHww&ixlib=rb-4.1.0&q=85&w=1200",
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Eyebrow = ({ children }) => (
  <p className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-amber">
    {children}
  </p>
);

const ADVANTAGE_ICONS = [Search, Layers, Compass, FileText];

export default function Home() {
  const { t, tList, lang } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const dossier = tList("home.hero.dossierItems");
  const aboutList = tList("home.about.list");
  const steps = tList("home.how.steps");
  const advantages = tList("home.advantages.items");
  const asideItems = tList("home.contact.asideItems");
  const faqItems = tList("home.faq.items");

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 82;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const validate = () => {
    const next = {};
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name) next.name = t("home.contact.errors.nameRequired");
    else if (name.length < 2) next.name = t("home.contact.errors.nameShort");
    if (!email) next.email = t("home.contact.errors.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
      next.email = t("home.contact.errors.emailInvalid");
    if (!message) next.message = t("home.contact.errors.messageRequired");
    else if (message.length < 10)
      next.message = t("home.contact.errors.messageShort");
    return next;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    try {
      window.sessionStorage.setItem(
        "dbdg_last_request",
        JSON.stringify({ ...form, at: new Date().toISOString(), lang }),
      );
    } catch (err) {
      /* storage unavailable */
    }
    window.setTimeout(() => navigate(THANK_YOU_PATH), 250);
  };

  const field =
    "mt-1.5 w-full rounded-xl border bg-white px-3.5 py-3 text-[0.9rem] text-ink outline-none transition-colors duration-200 placeholder:text-muted-ink/45 focus:border-ink";

  return (
    <div>
      <Seo
        title={t("meta.home.title")}
        description={t("meta.home.description")}
        htmlLang={t("lang.htmlLang")}
        canonical={`${ORIGIN}${langPath(lang)}`}
        alternates={[
          { hreflang: "es-AR", href: `${ORIGIN}${langPath("ar")}` },
          { hreflang: "en", href: `${ORIGIN}${langPath("en")}` },
        ]}
      />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden border-b border-hairline">
        <div className="pointer-events-none absolute inset-0 hero-wash" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-3 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-muted-ink">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber" aria-hidden="true" />
                  {t("home.hero.badge")}
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-6 max-w-[22ch] font-display text-[2.5rem] font-semibold leading-[1.04] tracking-[-0.025em] text-ink sm:text-[3.4rem] lg:text-[4rem]">
                  {t("home.hero.title")}
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-[58ch] text-[1rem] leading-7 text-muted-ink">
                  {t("home.hero.lead")}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => scrollTo("contacto")}
                    data-testid="hero-primary-cta"
                    className="inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-3 text-[0.88rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2 active:translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2"
                  >
                    {t("home.hero.primaryCta")}
                    <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollTo("como-funciona")}
                    data-testid="hero-secondary-cta"
                    className="inline-flex items-center gap-2 rounded-xl border border-hairline bg-white px-5 py-3 text-[0.88rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
                  >
                    {t("home.hero.secondaryCta")}
                  </button>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-12 overflow-hidden rounded-2xl border border-hairline bg-white">
                  <img
                    src={IMG.hero}
                    alt={t("home.hero.photoAlt")}
                    loading="eager"
                    className="h-[240px] w-full object-cover sm:h-[300px]"
                  />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-2xl border border-hairline bg-white p-6 shadow-card">
                  <div className="flex items-baseline justify-between gap-4 border-b border-hairline2 pb-4">
                    <p className="font-display text-[1.15rem] font-semibold text-ink">
                      {t("home.hero.dossierTitle")}
                    </p>
                    <span className="font-mono text-[0.58rem] uppercase tracking-[0.14em] text-muted-ink/70">
                      {t("common.domain")}
                    </span>
                  </div>
                  <dl className="mt-1">
                    {dossier.map((d) => (
                      <div
                        key={d.label}
                        className="flex items-center justify-between gap-4 border-b border-hairline2 py-3.5 last:border-0"
                      >
                        <dt className="text-[0.84rem] text-muted-ink">{d.label}</dt>
                        <dd className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-ink">
                          {d.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-4 text-[0.75rem] leading-5 text-muted-ink/85">
                    {t("home.hero.dossierNote")}
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="mt-6 hidden font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/60 lg:block">
                  {t("home.hero.scrollHint")}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section id="servicio" className="scroll-mt-24 border-b border-hairline">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <Eyebrow>{t("home.about.eyebrow")}</Eyebrow>
            <h2 className="mt-4 max-w-[26ch] font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[2.6rem]">
              {t("home.about.title")}
            </h2>
          </Reveal>

          <div className="mt-10 grid gap-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="max-w-[68ch] text-[0.98rem] leading-7 text-muted-ink">
                  {t("home.about.body")}
                </p>
              </Reveal>
              <Reveal delay={0.06}>
                <blockquote className="my-9 border-l-2 border-amber pl-5">
                  <p className="max-w-[46ch] font-display text-[1.3rem] font-medium leading-snug text-ink sm:text-[1.5rem]">
                    {t("home.about.pullQuote")}
                  </p>
                </blockquote>
              </Reveal>
              <Reveal delay={0.1}>
                <h3 className="max-w-[30ch] font-display text-[1.45rem] font-semibold leading-snug text-ink">
                  {t("home.about.secondTitle")}
                </h3>
                <p className="mt-4 max-w-[68ch] text-[0.98rem] leading-7 text-muted-ink">
                  {t("home.about.secondBody")}
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="mt-8 overflow-hidden rounded-2xl border border-hairline">
                  <img
                    src={IMG.about}
                    alt={t("home.about.photoAlt")}
                    loading="lazy"
                    className="h-[220px] w-full object-cover sm:h-[280px]"
                  />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.08}>
                <div className="rounded-2xl border border-hairline bg-paper2 p-6">
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
                    {t("home.about.listTitle")}
                  </p>
                  <ul className="mt-4">
                    {aboutList.map((item) => (
                      <li
                        key={item}
                        className="flex gap-3 border-b border-hairline py-3.5 last:border-0"
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0 text-amber"
                          strokeWidth={2}
                        />
                        <span className="text-[0.86rem] leading-6 text-muted-ink">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => scrollTo("contacto")}
                    data-testid="about-cta"
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-ink/15 bg-white px-4 py-3 text-[0.85rem] font-semibold text-ink transition-colors duration-200 hover:bg-paper"
                  >
                    {t("home.about.cta")}
                    <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section
        id="como-funciona"
        className="scroll-mt-24 border-b border-hairline bg-paper2"
      >
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>{t("home.how.eyebrow")}</Eyebrow>
                <h2 className="mt-4 max-w-[20ch] font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[2.5rem]">
                  {t("home.how.title")}
                </h2>
                <p className="mt-5 max-w-[46ch] text-[0.95rem] leading-7 text-muted-ink">
                  {t("home.how.lead")}
                </p>
                <button
                  type="button"
                  onClick={() => scrollTo("contacto")}
                  data-testid="how-cta"
                  className="mt-7 inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-3 text-[0.86rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2"
                >
                  {t("home.how.cta")}
                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <ol className="relative border-l border-hairline">
                {steps.map((s, i) => (
                  <li key={s.title} className="relative pb-8 pl-8 last:pb-0">
                    <Reveal delay={i * 0.06}>
                      <span className="absolute -left-[13px] top-0 flex h-6 w-6 items-center justify-center rounded-full border border-hairline bg-white font-mono text-[0.62rem] font-semibold text-ink">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="max-w-[34ch] font-display text-[1.2rem] font-semibold leading-snug text-ink">
                        {s.title}
                      </h3>
                      <p className="mt-2 max-w-[60ch] text-[0.9rem] leading-7 text-muted-ink">
                        {s.body}
                      </p>
                    </Reveal>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ================= ADVANTAGES ================= */}
      <section id="ventajas" className="scroll-mt-24 border-b border-hairline">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <Eyebrow>{t("home.advantages.eyebrow")}</Eyebrow>
            <h2 className="mt-4 max-w-[26ch] font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[2.5rem]">
              {t("home.advantages.title")}
            </h2>
            <p className="mt-5 max-w-[60ch] text-[0.95rem] leading-7 text-muted-ink">
              {t("home.advantages.lead")}
            </p>
          </Reveal>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Reveal className="lg:col-span-1 lg:row-span-2">
              <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-hairline bg-paper2">
                <img
                  src={IMG.workspace}
                  alt={t("home.advantages.highlightPhotoAlt")}
                  loading="lazy"
                  className="h-[190px] w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-6">
                  <ShieldCheck className="h-5 w-5 text-amber" strokeWidth={1.75} />
                  <h3 className="mt-4 max-w-[24ch] font-display text-[1.25rem] font-semibold leading-snug text-ink">
                    {t("home.advantages.highlightTitle")}
                  </h3>
                  <p className="mt-3 text-[0.88rem] leading-7 text-muted-ink">
                    {t("home.advantages.highlightBody")}
                  </p>
                </div>
              </div>
            </Reveal>

            {advantages.map((a, i) => {
              const Icon = ADVANTAGE_ICONS[i % ADVANTAGE_ICONS.length];
              return (
                <Reveal key={a.title} delay={0.05 * i}>
                  <div
                    data-testid={`advantage-card-${i}`}
                    className="h-full rounded-2xl border border-hairline bg-white p-6 transition-shadow duration-200 hover:shadow-cardHover"
                  >
                    <Icon className="h-5 w-5 text-ink" strokeWidth={1.75} />
                    <h3 className="mt-4 max-w-[26ch] font-display text-[1.1rem] font-semibold leading-snug text-ink">
                      {a.title}
                    </h3>
                    <p className="mt-2.5 text-[0.86rem] leading-6 text-muted-ink">
                      {a.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= CONTACT FORM ================= */}
      <section
        id="contacto"
        className="scroll-mt-24 border-b border-hairline bg-paper2"
      >
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <Eyebrow>{t("home.contact.eyebrow")}</Eyebrow>
            <h2 className="mt-4 max-w-[24ch] font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[2.5rem]">
              {t("home.contact.title")}
            </h2>
            <p className="mt-5 max-w-[72ch] text-[0.93rem] leading-7 text-muted-ink">
              {t("home.contact.lead")}
            </p>
          </Reveal>

          <div className="mt-10 grid gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="rounded-2xl border border-hairline bg-white p-6 shadow-card sm:p-8">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
                  {t("home.contact.formTitle")}
                </p>
                <form onSubmit={onSubmit} noValidate className="mt-5" data-testid="contact-form">
                  <div>
                    <label
                      htmlFor="name"
                      className="text-[0.8rem] font-medium text-ink"
                    >
                      {t("home.contact.nameLabel")}
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      placeholder={t("home.contact.namePlaceholder")}
                      data-testid="contact-name-input"
                      aria-invalid={Boolean(errors.name)}
                      className={`${field} ${errors.name ? "border-danger" : "border-hairline"}`}
                    />
                    {errors.name && (
                      <p
                        data-testid="contact-name-error"
                        className="mt-1.5 text-[0.76rem] text-danger"
                      >
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="mt-5">
                    <label
                      htmlFor="email"
                      className="text-[0.8rem] font-medium text-ink"
                    >
                      {t("home.contact.emailLabel")}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, email: e.target.value }))
                      }
                      placeholder={t("home.contact.emailPlaceholder")}
                      data-testid="contact-email-input"
                      aria-invalid={Boolean(errors.email)}
                      className={`${field} ${errors.email ? "border-danger" : "border-hairline"}`}
                    />
                    {errors.email && (
                      <p
                        data-testid="contact-email-error"
                        className="mt-1.5 text-[0.76rem] text-danger"
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="mt-5">
                    <label
                      htmlFor="message"
                      className="text-[0.8rem] font-medium text-ink"
                    >
                      {t("home.contact.messageLabel")}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, message: e.target.value }))
                      }
                      placeholder={t("home.contact.messagePlaceholder")}
                      data-testid="contact-message-input"
                      aria-invalid={Boolean(errors.message)}
                      className={`${field} resize-y ${errors.message ? "border-danger" : "border-hairline"}`}
                    />
                    {errors.message && (
                      <p
                        data-testid="contact-message-error"
                        className="mt-1.5 text-[0.76rem] text-danger"
                      >
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-5 py-3.5 text-[0.88rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2 disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2"
                  >
                    {submitting
                      ? t("home.contact.submitting")
                      : t("home.contact.submit")}
                    {!submitting && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
                  </button>

                  <p className="mt-4 text-[0.74rem] leading-5 text-muted-ink/85">
                    {t("home.contact.consentNote")}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <Link
                      to={langPath(lang, "privacy")}
                      data-testid="contact-privacy-link"
                      className="link-underline text-[0.74rem] text-ink"
                    >
                      {t("nav.privacy")}
                    </Link>
                    <Link
                      to={langPath(lang, "terms")}
                      data-testid="contact-terms-link"
                      className="link-underline text-[0.74rem] text-ink"
                    >
                      {t("nav.terms")}
                    </Link>
                  </div>
                </form>
              </div>
            </Reveal>

            <Reveal delay={0.08} className="lg:col-span-5">
              <div className="h-full rounded-2xl border border-hairline bg-white p-6 sm:p-7">
                <p className="font-display text-[1.15rem] font-semibold text-ink">
                  {t("home.contact.asideTitle")}
                </p>
                <ol className="mt-4">
                  {asideItems.map((item, i) => (
                    <li
                      key={item}
                      className="flex gap-3 border-b border-hairline2 py-3.5 last:border-0"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-paper2 font-mono text-[0.58rem] font-semibold text-ink">
                        {i + 1}
                      </span>
                      <span className="text-[0.86rem] leading-6 text-muted-ink">
                        {item}
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-5 border-l-2 border-amber bg-paper2 px-4 py-3 text-[0.8rem] leading-6 text-ink">
                  {t("home.contact.asideNote")}
                </p>

                <div className="mt-7 border-t border-hairline pt-6">
                  <p className="text-[0.9rem] font-semibold text-ink">
                    {t("home.contact.directTitle")}
                  </p>
                  <p className="mt-1.5 text-[0.82rem] leading-6 text-muted-ink">
                    {t("home.contact.directBody")}
                  </p>
                  <a
                    href={`mailto:${t("home.contact.directCta")}`}
                    data-testid="contact-mailto"
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-hairline px-4 py-2.5 text-[0.82rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2"
                  >
                    <Mail className="h-4 w-4 text-amber" strokeWidth={1.75} />
                    {t("home.contact.directCta")}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="scroll-mt-24 border-b border-hairline">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Eyebrow>{t("home.faq.eyebrow")}</Eyebrow>
                <h2 className="mt-4 max-w-[18ch] font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[2.4rem]">
                  {t("home.faq.title")}
                </h2>
                <p className="mt-5 max-w-[40ch] text-[0.92rem] leading-7 text-muted-ink">
                  {t("home.faq.lead")}
                </p>
                <a
                  href={`mailto:${t("footer.emailGeneral")}`}
                  data-testid="faq-mailto"
                  className="mt-6 inline-flex items-center gap-2 rounded-xl border border-hairline px-4 py-2.5 text-[0.82rem] font-medium text-ink transition-colors duration-200 hover:bg-paper2"
                >
                  <Mail className="h-4 w-4 text-amber" strokeWidth={1.75} />
                  {t("common.writeUs")}
                </a>
              </Reveal>
            </div>

            <div className="lg:col-span-8" data-testid="faq-accordion">
              {faqItems.map((item, i) => {
                const open = openFaq === i;
                return (
                  <div key={item.q} className="border-b border-hairline">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      aria-expanded={open}
                      data-testid={`faq-question-${i}`}
                      className="flex w-full items-start justify-between gap-5 py-5 text-left"
                    >
                      <span className="max-w-[52ch] text-[0.95rem] font-semibold text-ink">
                        {item.q}
                      </span>
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-hairline text-ink">
                        {open ? (
                          <Minus className="h-3.5 w-3.5" strokeWidth={2} />
                        ) : (
                          <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                        )}
                      </span>
                    </button>
                    {open && (
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="max-w-[68ch] pb-6 pr-8 text-[0.89rem] leading-7 text-muted-ink"
                      >
                        {item.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ================= POLICIES ================= */}
      <section className="bg-paper2">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Reveal>
            <Eyebrow>{t("home.policies.eyebrow")}</Eyebrow>
            <h2 className="mt-4 max-w-[26ch] font-display text-[1.85rem] font-semibold leading-[1.12] tracking-[-0.02em] text-ink sm:text-[2.2rem]">
              {t("home.policies.title")}
            </h2>
            <p className="mt-4 max-w-[58ch] text-[0.92rem] leading-7 text-muted-ink">
              {t("home.policies.lead")}
            </p>
          </Reveal>

          <div className="mt-9 grid gap-4 md:grid-cols-3">
            {[
              { slug: "privacy", titleKey: "nav.privacy", bodyKey: "home.policies.privacyCard", testId: "policies-privacy-card" },
              { slug: "cookies", titleKey: "nav.cookies", bodyKey: "home.policies.cookiesCard", testId: "policies-cookies-card" },
              { slug: "terms", titleKey: "nav.terms", bodyKey: "home.policies.termsCard", testId: "policies-terms-card" },
            ].map((c, i) => (
              <Reveal key={c.slug} delay={0.05 * i}>
                <Link
                  to={langPath(lang, c.slug)}
                  data-testid={c.testId}
                  className="group flex h-full flex-col rounded-2xl border border-hairline bg-white p-6 transition-shadow duration-200 hover:shadow-cardHover"
                >
                  <h3 className="font-display text-[1.12rem] font-semibold leading-snug text-ink">
                    {t(c.titleKey)}
                  </h3>
                  <p className="mt-2.5 flex-1 text-[0.85rem] leading-6 text-muted-ink">
                    {t(c.bodyKey)}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-[0.8rem] font-semibold text-ink">
                    {t("home.policies.cardCta")}
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1"
                      strokeWidth={2}
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
